import React, {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import {
  Box,
  Button,
  Snackbar,
  Typography,
  Paper,
  Alert,
  Grid2,
  IconButton,
  Chip,
  Tooltip,
  styled,
} from "@mui/material";
// import { Box, Button, Snackbar, , Typography } from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import StarIcon from "@mui/icons-material/Star";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import makeFullUrl from "../../utils/cloudinary";

// Hidden input to upload images
// const VisuallyHiddenInput = styled("input")({
//   clip: "rect(0 0 0 0)",
//   clipPath: "inset(50%)",
//   height: 1,
//   overflow: "hidden",
//   position: "absolute",
//   bottom: 0,
//   left: 0,
//   whiteSpace: "nowrap",
//   width: 1,
// });

const UploadImageForm = forwardRef(
  ({ onDataChange, imageInfo, initialData }, ref) => {
    // Handle image change functionality STARTS
    const [selectedImages, setSelectedImages] = useState([]);

    useEffect(() => {
      if (!initialData) return;

      if (selectedImages.length > 0) {
        return;
      }

      // Create stable initial images array
      const initialImages = (initialData.images || []).map((img) => ({
        ...img,
        url: img.filePath
          ? // ? `${process.env.REACT_APP_SERVER_URL}/${img.filePath}`
            makeFullUrl(img.filePath, img.fileType)
          : URL.createObjectURL(img.file),
        isMain: false,
      }));

      // Handle main image
      const mainImage = initialData.mainImage
        ? {
            ...initialData.mainImage,
            url: initialData.mainImage.filePath
              ? // ? `${process.env.REACT_APP_SERVER_URL}/${initialData.mainImage.filePath}`
                makeFullUrl(
                  initialData.mainImage.filePath,
                  initialData.mainImage.fileType,
                )
              : URL.createObjectURL(initialData.mainImage.file),
            isMain: true,
          }
        : null;

      setSelectedImages(
        mainImage ? [mainImage, ...initialImages] : initialImages,
      );
    }, [initialData]);

    // Prevent memory leaks by revoking object URLs when images are removed or the component unmounts
    useEffect(() => {
      return () => {
        selectedImages.forEach((image) => {
          if (image.url.startsWith("blob:")) {
            URL.revokeObjectURL(image.url);
          }
        });
      };
    }, []);

    const [errors, setErrors] = useState({
      image: false,
      imageType: false,
      mainImage: false,
    });

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");

    // const [snackbarImageSizeOpen, setSnackbarImageSizeOpen] = useState(false);
    // const [fileSizeName, setFileSizeName] = useState([]);

    const uploadButtonRef = useRef(null);
    const mainImageErrorRef = useRef(null); // Ref for main image error message

    const validImageTypes = [
      "image/jpeg",
      "image/png",
      // "image/gif",
      "image/bmp",
      "image/webp",
    ];
    const MAX_IMAGES = 6;
    const MAX_FILE_SIZE_MB = 5 * 1024 * 1024; // 5MB

    // Handle image selection
    const handleImageChange = useCallback(
      async (event) => {
        const files = Array.from(event.target.files);

        // 1. Check if more than 6 images are selected
        if (selectedImages.length + files.length > MAX_IMAGES) {
          // Show snackbar if trying to upload more than 6 images
          showSnackbar(`You can upload a maximum of ${MAX_IMAGES} images`);
          return;
        }

        // Filter valid images
        const validFiles = files.filter((file) =>
          validImageTypes.includes(file.type),
        );

        // If there are invalid files, alert the user and clear the input
        if (validFiles.length !== files.length) {
          setErrors((prev) => ({ ...prev, imageType: true }));
          uploadButtonRef.current.focus();
          return; // Prevent submission if no images are selected
        } else {
          setErrors((prev) => ({ ...prev, imageType: false }));
        }

        // 3. Check image size (5 MB limit)
        const oversizedFiles = validFiles.filter(
          (file) => file.size >= MAX_FILE_SIZE_MB,
        );

        // if (oversizedFiles.length > 0) {
        //   setFileSizeName(oversizedFiles.map((file) => file.name));
        //   setSnackbarImageSizeOpen(true);
        //   return; // Prevent submission if image size is greater than 5 MB
        // }

        if (oversizedFiles.length > 0) {
          showSnackbar(
            `Some images exceed 5MB: ${oversizedFiles
              .map((f) => f.name)
              .join(", ")}`,
          );
          return;
        }

        // // Convert images to WebP
        // const webpFiles = await Promise.all(
        //   validFiles.map((file) => convertToWebP(file))
        // );

        // // Add new images to the state
        // const newImages = webpFiles.map((file) => ({
        //   url: URL.createObjectURL(file),
        //   file,
        //   isMain: false, // Explicitly set initial state
        // }));

        // // Update state
        // setSelectedImages((prevImages) => [...prevImages, ...newImages]);

        // setErrors((prev) => ({ ...prev, image: false }));

        // Convert to WebP
        try {
          const webpFiles = await Promise.all(
            validFiles.map((file) => convertToWebP(file)),
          );

          const newImages = webpFiles.map((file) => ({
            url: URL.createObjectURL(file),
            file,
            isMain: false,
          }));

          setSelectedImages((prev) => [...prev, ...newImages]);
          setErrors((prev) => ({ ...prev, image: false }));
        } catch (error) {
          showSnackbar("Failed to process images");
        }
      },
      [selectedImages],
    );

    // Handle image removal
    const handleRemoveImage = useCallback((index) => {
      setSelectedImages((prevImages) => {
        const removedImage = prevImages[index];
        const updatedImages = prevImages.filter((_, i) => i !== index);

        if (removedImage.isMain) {
          setErrors((prev) => ({ ...prev, mainImage: true }));
        }

        return updatedImages;
      });
    }, []); // Dependency ensures latest mainImage value

    // Set main image
    // const handleSetMainImage = useCallback(
    //   (index) => {
    //     const newMainImage = selectedImages[index].file;
    //     setMainImage(newMainImage);
    //     setErrors((prev) => ({ ...prev, mainImage: false }));

    //     // onDataChange({
    //     //   images: selectedImages.filter((img) => img.file !== newMainImage),
    //     //   mainImage: newMainImage,
    //     // });
    //   },
    //   // [selectedImages, onDataChange]
    //   [selectedImages]
    // );

    const handleSetMainImage = useCallback((index) => {
      setSelectedImages((prev) =>
        prev.map((img, i) => ({ ...img, isMain: i === index })),
      );
      setErrors((prev) => ({ ...prev, mainImage: false }));
    }, []);

    // Close snackbars
    const handleSnackbarClose = useCallback(() => {
      setSnackbarOpen(false);
      // setSnackbarImageSizeOpen(false);
    }, []);
    // Handle image change functionality ENDS

    const showSnackbar = (message) => {
      setSnackbarMessage(message);
      setSnackbarOpen(true);
    };

    // Convert images to webp format
    // async function convertToWebP(file) {
    const convertToWebP = async (file) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);

          canvas.toBlob((blob) => {
            // Create a new File object with a specific name
            const webpFile = new File(
              [blob],
              file.name.replace(/\.\w+$/, ".webp"),
              { type: "image/webp" },
            );
            resolve(webpFile);
          }, "image/webp");
        };
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
      });
    };

    // Validate all form fields on submit
    const validateUserInput = useCallback(() => {
      // 1. Check if any image selected to upload & main image is set
      if (selectedImages.length === 0) {
        setErrors((prev) => ({ ...prev, image: true }));
        uploadButtonRef.current.focus();
        return false; // Prevent submission if no images are selected
      }

      // 2. Check if main image is selected using the actual data
      const hasMainImage = selectedImages.some((img) => img.isMain);

      if (!hasMainImage) {
        setErrors((prev) => ({ ...prev, mainImage: true }));
        // uploadButtonRef.current.focus();
        mainImageErrorRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        return false; // Prevent submission if main image is not selected
      }
      return true;
    }, [selectedImages]);

    useEffect(() => {
      const mainImageData = selectedImages.find((img) => img.isMain);
      const otherImages = selectedImages.filter((img) => !img.isMain);

      const newData = {
        images: otherImages,
        mainImage: mainImageData || null,
      };

      if (JSON.stringify(newData) !== JSON.stringify(prevDataRef.current)) {
        onDataChange(newData);
        prevDataRef.current = newData;
      }
    }, [selectedImages, onDataChange]);

    // Expose the validate method to the parent via ref
    useImperativeHandle(ref, () => ({
      validateUserInput,
    }));

    // Initialize a ref to store the previous data
    const prevDataRef = useRef({
      images: [],
      mainImage: null,
    });

    // Scroll to main image error when needed
    useEffect(() => {
      if (errors.mainImage && mainImageErrorRef.current) {
        mainImageErrorRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, [errors.mainImage]);

    return (
      <Box
        sx={{
          width: "100%",
          maxWidth: 800,
          margin: "0 auto",
        }}
      >
        {/* Main Paper Container */}
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          {/* Header */}
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 600,
              // mb: 1,
            }}
          >
            {imageInfo || "Upload Images"}
            {/* Property Photos */}
          </Typography>

          {/* Image Count & Requirements */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Upload high-quality photos.{" "}
              {/* The first image will be used as the main display image. */}
              Set main image to display it as a default image.
            </Typography>
            <Grid2 container spacing={1} sx={{ mt: 1 }}>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary">
                  • Minimum 1 image
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary">
                  • Maximum {MAX_IMAGES} images
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary">
                  • Maximum file size: 5MB each
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary">
                  • Supported formats: JPEG, PNG, BMP, WebP
                </Typography>
              </Grid2>
              {/* <Grid2 size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    • Recommended size: 1200x800 pixels
                  </Typography>
                </Grid2> */}
            </Grid2>
          </Box>

          {/* Upload Button */}
          <Box sx={{ mb: 3, textAlign: "center" }}>
            <Button
              component="label"
              variant="outlined"
              startIcon={<AddPhotoAlternateIcon />}
              color="inherit"
              sx={{ px: 4, py: 1.5 }}
              disabled={selectedImages.length >= MAX_IMAGES}
              ref={uploadButtonRef}
            >
              Upload Photos
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageChange}
                style={{ display: "none" }}
                //   ref={fileInputRef}
              />
            </Button>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              {selectedImages.length} of {MAX_IMAGES} images uploaded
            </Typography>
          </Box>

          {/* Error Messages */}
          {errors.image && (
            <Alert severity="error" sx={{ mb: 3 }}>
              Please upload at least one image
            </Alert>
          )}
          {errors.imageType && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {/* Please upload only image files (jpg, jpeg, png, bmp, webp). */}
              Only JPG, JPEG, PNG, BMP, and WebP images are allowed
            </Alert>
          )}

          {errors.mainImage && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              ref={mainImageErrorRef}
              action={
                <IconButton
                  size="small"
                  onClick={() =>
                    setErrors((prev) => ({ ...prev, mainImage: false }))
                  }
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              Please set a main image. Click "Set as Main" on your best photo.
            </Alert>
          )}

          {/* Image Preview Grid */}
          {selectedImages.length > 0 && (
            <Box>
              {/* <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                  Preview ({selectedImages.length} images)
                </Typography> */}

              <Grid2 container spacing={2}>
                {selectedImages.map((image, index) => (
                  <Grid2 key={index} size={{ xs: 6, sm: 4, md: 4 }}>
                    <Paper
                      elevation={1}
                      sx={{
                        position: "relative",
                        overflow: "hidden",
                        borderRadius: 2,
                        border: image.isMain
                          ? "2px solid #1976d2"
                          : "1px solid #e0e0e0",
                        height: 200,
                      }}
                    >
                      {/* Main Image Badge */}
                      {image.isMain && (
                        <Chip
                          label="Main"
                          size="small"
                          color="primary"
                          icon={<StarIcon fontSize="small" />}
                          sx={{
                            position: "absolute",
                            top: 8,
                            left: 8,
                            zIndex: 1,
                          }}
                        />
                      )}

                      {/* Image */}
                      <Box
                        component="img"
                        src={image.url}
                        alt={`Preview ${index + 1}`}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          transition: "transform 0.3s",
                          "&:hover": {
                            transform: "scale(1.05)",
                          },
                        }}
                      />

                      {/* Action Buttons Overlay */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          bgcolor: "rgba(0, 0, 0, 0.5)",
                          opacity: 0,
                          transition: "opacity 0.3s",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1,
                          "&:hover": {
                            opacity: 1,
                          },
                        }}
                      >
                        {/* Set as Main Button */}
                        {!image.isMain && (
                          <Tooltip title="Set as main image">
                            <IconButton
                              size="small"
                              onClick={() => handleSetMainImage(index)}
                              sx={{
                                bgcolor: "primary.main",
                                color: "white",
                                "&:hover": { bgcolor: "primary.dark" },
                              }}
                            >
                              <StarIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {/* Remove Button */}
                        <Tooltip title="Remove image">
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveImage(index)}
                            sx={{
                              bgcolor: "error.main",
                              color: "white",
                              "&:hover": { bgcolor: "error.dark" },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Paper>

                    {/* Image Info */}
                    <Box sx={{ mt: 1, textAlign: "center" }}>
                      <Typography variant="caption" color="text.secondary">
                        Image {index + 1}
                      </Typography>
                    </Box>
                  </Grid2>
                ))}

                {/* Add More Button Tile */}
                {selectedImages.length < MAX_IMAGES && (
                  <Grid2 size={{ xs: 6, sm: 4, md: 4 }}>
                    <Paper
                      elevation={1}
                      sx={{
                        height: 200,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "2px dashed #ccc",
                        borderRadius: 2,
                        cursor: "pointer",
                        transition: "border-color 0.3s",
                        "&:hover": {
                          borderColor: "primary.main",
                          bgcolor: "action.hover",
                        },
                      }}
                      onClick={() => uploadButtonRef.current?.click()}
                    >
                      <Box sx={{ textAlign: "center" }}>
                        <Box>
                          <AddPhotoAlternateIcon
                            sx={{
                              fontSize: 40,
                              color: "text.secondary",
                              mb: 1,
                            }}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          Add More
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          {MAX_IMAGES - selectedImages.length} left
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid2>
                )}
              </Grid2>

              {/* Instructions */}
              {/* <Box sx={{ mt: 3, textAlign: "center" }}>
                  <Typography variant="caption" color="text.secondary">
                    Click on an image to view larger. Drag and drop to reorder
                    (coming soon).
                  </Typography>
                </Box> */}
            </Box>
          )}

          {/* No Images State */}
          {selectedImages.length === 0 && (
            <Paper
              variant="outlined"
              sx={{
                p: 6,
                textAlign: "center",
                border: "2px dashed #ccc",
                borderRadius: 2,
                bgcolor: "background.default",
              }}
            >
              <PhotoCameraIcon
                sx={{ fontSize: 60, color: "text.secondary", mb: 2 }}
              />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No images uploaded yet
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Upload photos to showcase your property
              </Typography>
            </Paper>
          )}
        </Paper>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity="warning"
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    );
  },
);

export default UploadImageForm;
